import { Directive, HostBinding, Input, OnInit } from '@angular/core';

import { MenuItemCounter, MenuItemsService } from '@frontend/vanilla/core';

/**
 * @stable
 */
export enum BadgeType {
    Counter = 'counter',
    Icon = 'icon',
}

/**
 * @whatItDoes Displays a counter or icon for specified item.
 *
 * @howToUse
 *
 * ```
 * <span vnMenuItemBadge section="Header" item="inbox" badgeClass="theme-spot-filled" badgeType="counter"></span>
 * ```
 *
 * @description
 *
 * {@link BadgeType.Counter} is displayed based on the value set by {@link MenuItemsService}.
 *
 * @param section string to indicate in which feature the badge is part of: e.g. {@link MenuSection.Header}.
 * @param item string to indicate the badge name.
 * @param badgeClass string indicate if the badge should be displays as dot without counter. Use class 'theme-spot-filled'.
 * @param badgeType string | undefined to indicate the badge type: e.g. {@link BadgeType.Icon}; {@link BadgeType.Counter} by default. If `badgeType` is provided, badge will be always visible.
 *
 * @stable
 */
@Directive({
    standalone: true,
    selector: 'ds-notification-bubble[vnMenuItemBadge]',
})
export class MenuItemBadgeDirective implements OnInit {
    @Input() section: string;
    @Input() item: string;
    @Input() badgeClass?: string | string[];
    @Input() badgeType?: string;

    private counter: MenuItemCounter | null;

    @HostBinding('textContent') get text() {
        const badgeType = this.counter?.type || this.badgeType || BadgeType.Counter;
        if (badgeType === BadgeType.Counter) {
            if (
                this.badgeClass?.includes('theme-spot-filled') ||
                this.counter?.count === undefined ||
                (this.counter?.count > 9 && (this.item === 'avatar' || this.item === 'avatarbalance'))
            ) {
                return '';
            } else {
                return this.counter?.count;
            }
        }
    }

    @HostBinding() get hidden(): boolean {
        const badgeType = this.counter?.type || this.badgeType || BadgeType.Counter;
        let hide = false;
        if (badgeType === BadgeType.Icon) {
            hide = !this.counter?.cssClass;
        } else {
            hide = !this.counter?.count;
        }
        return hide;
    }

    constructor(private menuItemsService: MenuItemsService) {}

    ngOnInit() {
        this.counter = this.menuItemsService.getCounter(this.section, this.item);
    }
}
